/* eslint-disable no-use-before-define */
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import {
  TREATMENT_TYPES_IDS as TTI,
  SERVICES,
  TREATMENT_TYPES as TT,
} from "@app/constants";
import { loadUserCalendarCreateOptions, loadAllStudentsNoInactive } from "@app/routes/users/api";
import {
  itemArrayToSelectOptions,
  idArrayToDict,
  setUniqueServicesFromMultipleTherapists, convertToNearest15Minutes,
} from "@amplio-common/funcs/commonFunctionalities";
import {
  CREATE_SESSION_MODES as CSM,
  TIMEZONE_OPTIONS,
  recUpdateTypes,
  defaultDyslexiaSessionDuration,
} from "../constants";
import {
  createTreatment,
  saveTreatmentActivityList,
  rescheduleTreatment,
  createEventTemplates,
  updateEventTemplates,
} from "@amplio-common/treatments/api";
import {
  parseISO,
  addMinutes,
  differenceInMinutes,
  setHours,
  setMinutes,
  addDays,
  setDate,
  isEqual,
  format,
} from "date-fns";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { trackSessionEvent } from "@app/store/analytics/createSession";
import { currentUser } from "@app/routes/auth/selectors";
import { gamesToActiviyListGames, selectGames } from "@app/routes/treatmentV3/store/activityLibrarySlice";
import { SLICE_NAMES } from "@app/routes/treatmentV3/store/sliceNames";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { simpleSetSubField } from "@app/routes/treatmentV3/constants";

import moment from "moment-timezone";

const CREATE_SESSION_ROOT_SELECTOR = state => state.createSession;
const CREATE_SESSION_SELECTOR = (s) => createSelector(CREATE_SESSION_ROOT_SELECTOR, s);
const simpleFieldSelector = (stateField) => CREATE_SESSION_SELECTOR(t => t[stateField]);
const simpleSubFieldSelector = (field, subField) => CREATE_SESSION_SELECTOR(t => t[field]?.[subField]);

const getDifferences = (iniObj, updObj) => {
  const result = {};
  Object.keys(updObj).forEach((field) => {
    if (!_.isEqual(updObj[field], iniObj[field])) {
      result[field] = updObj[field];
    }
  });
  return result;
};

const initialState = {
  therapist: {},
  initialSession: {},
  isLoadingOptions: true,
  isLoadingAllStudents: false,
  isLoadingCreate: false,
  isError: false,
  mode: null,
  typeId: null,
  serviceType: null,
  serviceOptions: null,
  startDate: null,
  startTime: null,
  endTime: null,
  directStudents: [],
  studentGroups: [],
  studentGroupsDict: {},
  initialParticipants: [],
  participants: [],
  allDistrictStudentsOptions: [],
  initialActivityPlaylist: [],
  activityPlaylist: [],
  makeUpDate: null,
  skipReasonId: null,
  skipReasonText: "",
  timezone: null,
  recurringSession: {},
  initialEventTemplate: {},
  isRecurring: false,
  recUpdateType: null,
  directTherapist: {},
  calendarType: null,
  studentTherapists: [],
  h5PActivityList: [],
  formattedGamePlayList: [],
  isGamePlayListReady: false,
  previewH5PGame: null,
  isPreviewH5PGameDone: false,
  selectedFilter: null,
  isOpen: false,
};

const simpleSetPayload = (stateField) => (state, action) => {
  state[stateField] = action.payload;
};
const setRecurrenceInfo = (recuField) => (state, action) => {
  state.recurringSession[recuField] = action.payload;
};

export const createGamesActivityList = createAsyncThunk(
  `${SLICE_NAMES.CREATE_SESSION}/getFormattedGameList`, async (payload, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState();
    // eslint-disable-next-line no-use-before-define
    const gamePlayList = selectActivityPlaylist(state);
    // eslint-disable-next-line no-use-before-define
    const allGameList = selectGames(state);

    const gameList = gamePlayList.map((playListGame) => {
      const game = allGameList.find(g => g.id === playListGame.gameId);
      const metadata = game?.game_metadata?.find(mt => mt.objective === playListGame.objectiveId);
      const requiredFormatGame = {
        ...game,
        ...metadata,
        uuid: uuidv4(),
      };
      if (game) {
        return requiredFormatGame;
      }

      return undefined;
    });
    return gameList;
  },
);


export const fetchCalendarOptions = createAsyncThunk(
  `${SLICE_NAMES.CREATE_SESSION}/fetchCalendarOptions`,
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const therapist = selectTherapist(state);

    const initialSession = selectInitialSession(state);
    const { response } = await loadUserCalendarCreateOptions(therapist.id, initialSession?.id);
    const studentsSet = new Set(response.students.map(s => s.id));

    const isMissingStudentFromInitial = (initialSession.students || []).some(s => !studentsSet.has(s.id));
    isMissingStudentFromInitial && thunkAPI.dispatch(fetchAllStudentOptions());

    return { ...response, isLoading: isMissingStudentFromInitial, therapist };
  },
);

export const fetchAllStudentOptions = createAsyncThunk(
  `${SLICE_NAMES.CREATE_SESSION}/fetchAllStudentOptions`,
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const therapist = selectTherapist(state);
    const { response } = await loadAllStudentsNoInactive(therapist.id);

    const directStudentIds = selectDirectStudentsDict(state);
    const districtGroups = {};
    response.forEach(stu => {
      if (directStudentIds[stu.id]) {
        return;
      } // is direct student
      const { district_name: districtName } = stu;
      const stuOption = { value: `s-${stu.id}`, label: stu.name };
      if (districtGroups[districtName]) {
        districtGroups[districtName].options.push(stuOption);
        return;
      }
      districtGroups[districtName] = { label: districtName, options: [stuOption] };
    });

    const allDistrictStudentsOptions = Object.values(districtGroups);
    payload?.(allDistrictStudentsOptions);

    return allDistrictStudentsOptions;
  },
);

export const fetchCalendarSessionCreate = createAsyncThunk(
  `${SLICE_NAMES.CREATE_SESSION}/fetchCalendarSessionCreate`,
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const mode = selectCreateSessionMode(state);
    const isRecurring = selectIsRecurring(state);
    const recUpdateType = selectRecUpdateType(state);
    const recSession = selectRecurringSession(state);

    const createApiCall = (isRecurring && recSession?.recurring_type) ? createEventTemplatesAPICall : createSessionAPICall;
    const updateApiCall = (recUpdateType === recUpdateTypes.FOLLOWING) ? updateEventTemplateAPICall : updateSessionAPICall;
    const response = await (mode === CSM.EDIT ? updateApiCall(thunkAPI) : createApiCall(thunkAPI));
    //Analytics
    const typeIdDict = response.type_id !== state.createSession.typeId ? { treatment_type: response.type_id } : {}; // in create session, the type id might turn into group session
    trackSessionEvent("calander_save_event", state, {
      treatment_id: response.id,
      treatment_create_type: mode,
      make_up: !!selectMakeUpDate(state),
      is_recurring: !!(recSession?.completed),
      ...typeIdDict,
    });

    payload?.(response);
    return response;
  },
);

const createSessionAPICall = async (thunkAPI) => {
  const state = thunkAPI.getState();
  const therapist = selectTherapist(state);
  const participants = selectParticipants(state);
  const directTherapist = selectDirectTherapist(state);
  const startTimeObj = parseISO(selectStartTime(state));
  const endTimeObj = parseISO(selectEndTime(state));
  const diff = differenceInMinutes(endTimeObj, startTimeObj);
  const timezone = selectTimezone(state) === TIMEZONE_OPTIONS.THERAPIST ? therapist.timezone : currentUser(state).timezone;
  const startDateTimeObj = zonedTimeToUtc(parseISO(selectStartDateTime(state)), timezone);
  const type = selectType(state);
  const makeUpDate = selectMakeUpDate(state);
  const service = selectServiceType(state);
  const h5pPlaylist = gamesToActiviyListGames(selectFormattedGamePlayList(state));
  const dyslexiaPlaylist = selectActivityPlaylist(state);
  const activityStack = service === SERVICES.SPEECH_LANGUAGE.id ? h5pPlaylist : dyslexiaPlaylist;

  //workaround for sys admins creating sessions for students and V4 pilot therapists
  let typeToUse = type.id;
  if (participants.length > 1 && type.id === TT.CLASS.id) {
    typeToUse = TT.GROUP_CLASS.id;
  }
  if (participants.length > 1 && type.id === TT.DEMO.id) {
    typeToUse = TT.GROUP_DEMO.id;
  }
  

  const data = {
    type_id: typeToUse,
    students: participants.filter(item => item.startsWith("s-")).map(item => item.replace("s-", "")),
    studentgroup_ids: participants.filter(item => item.startsWith("g-")).map(item => item.replace("g-", "")),
    start_date: startDateTimeObj.toISOString(),
    end_date: addMinutes(startDateTimeObj, diff).toISOString(),
    therapist: directTherapist?.id || therapist.id,
    service,
    make_up_for: makeUpDate ? format(parseISO(makeUpDate), "yyyy-MM-dd") : undefined,
    activity_stack: type.isDirect ? activityStack : undefined,
  };

  const { response } = await createTreatment(data, false);
  return response;
};

const createEventTemplatesAPICall = async (thunkAPI) => {
  const state = thunkAPI.getState();
  const type = selectType(state);
  const participants = selectParticipants(state);
  const therapist = selectTherapist(state);
  const directTherapist = selectDirectTherapist(state);
  const timezone = selectTimezone(state) === TIMEZONE_OPTIONS.THERAPIST ? therapist.timezone : currentUser(state).timezone;
  const start_date = selectStartDate(state);
  const start_time = selectStartTime(state);
  const end_time = selectEndTime(state);
  const diff = differenceInMinutes(parseISO(end_time), parseISO(start_time));
  const {
    day_of_week = [],
    end_date,
    recurring_type = null,
    recurring_interval = null,
    day_of_month = null,
    week_of_month = null,
  } = selectRecurringSession(state);
  const service = selectServiceType(state);
  const h5pPlaylist = gamesToActiviyListGames(selectFormattedGamePlayList(state));
  const dyslexiaPlaylist = selectActivityPlaylist(state);
  const activityStack = service === SERVICES.SPEECH_LANGUAGE.id ? h5pPlaylist : dyslexiaPlaylist;

  const startDateTimeObj = zonedTimeToUtc(parseISO(selectStartDateTime(state)), timezone);
  const endDateTimeObj = addMinutes(startDateTimeObj, diff);

  const options = { hour: "numeric", minute: "numeric", second: "numeric", timeZone: "UTC", hourCycle: "h23" };

  const startTime = new Intl.DateTimeFormat("default", options).format(new Date(startDateTimeObj.toISOString()));
  const endTime = new Intl.DateTimeFormat("default", options).format(new Date(endDateTimeObj.toISOString()));

  const activityPlaylist = type.isDirect ? activityStack : undefined;

  //workaround for sys admins creating sessions for students and V4 pilot therapists
  const typeToUse = type.id;

  const data = {
    type_id: `${typeToUse}`,
    students: participants.filter(item => item.startsWith("s-")).map(item => item.replace("s-", "")),
    start_date: format(parseISO(start_date), "yyyy-MM-dd"),
    end_date: format(parseISO(end_date), "yyyy-MM-dd"),
    start_time: startTime,
    end_time: endTime,
    recurring_pattern: {
      recurring_type,
      recurring_interval,
      day_of_week,
      day_of_month,
      week_of_month,
    },
    activity_stack: activityPlaylist,
    student_groups: participants.filter(item => item.startsWith("g-")).map(item => item.replace("g-", "")),

    is_recurring: true,
    therapist: directTherapist?.id || therapist.id,
    service,
  };

  const { response } = await createEventTemplates(data);
  return response;
};

const updateSessionAPICall = async (thunkAPI) => {
  const state = thunkAPI.getState();
  const therapist = selectTherapist(state);
  //const directTherapist = selectDirectTherapist(state);
  const participants = selectParticipants(state);
  const initialSession = selectInitialSession(state);
  const type = selectType(state);
  const service = selectServiceType(state);
  const h5pPlaylist = gamesToActiviyListGames(selectFormattedGamePlayList(state));
  const dyslexiaPlaylist = selectActivityPlaylist(state);
  const activityStack = service === SERVICES.SPEECH_LANGUAGE.id ? h5pPlaylist : dyslexiaPlaylist;
  const activityPlaylist = type.isDirect ? activityStack : undefined;
  const params = {
    single_event_change: true,
  };
  if (!selectIsInfoChanged(state) && selectIsActivityPlayListChange(state)) {
    const fixedActivityPlaylist = _.cloneDeep(activityPlaylist).map(item => { item.treatmentId = initialSession.id; return item; });
    const { response } = await saveTreatmentActivityList(fixedActivityPlaylist, initialSession.id, params);
    return response;
  }

  const nowDate = new Date();
  const startTimeObj = setDate(parseISO(selectStartTime(state)), nowDate.getDate());
  const endTimeObj = setDate(parseISO(selectEndTime(state)), nowDate.getDate());
  const diff = differenceInMinutes(endTimeObj, startTimeObj);
  const timezone = selectTimezone(state) === TIMEZONE_OPTIONS.THERAPIST ? therapist.timezone : currentUser(state).timezone;
  const startDateTimeObj = zonedTimeToUtc(parseISO(selectStartDateTime(state)), timezone);


  const data = {
    type_id: type.id,
    students: participants.filter(item => item.startsWith("s-")).map(item => item.replace("s-", "")),
    studentgroup_ids: participants.filter(item => item.startsWith("g-")).map(item => item.replace("g-", "")),
    start_date: startDateTimeObj.toISOString(),
    end_date: addMinutes(startDateTimeObj, diff).toISOString(),
    therapist: therapist.id,
    service,
    make_up_for: selectMakeUpDate(state) || undefined,
    activity_stack: activityPlaylist,
    skip_reason: selectSkipReasonText(state),
    skip_reason_id: selectSkipReasonId(state),
  };
  const { response } = await rescheduleTreatment(initialSession.id, data, false, params);
  return response;
};

export const updateEventTemplateAPICall = async (thunkAPI) => {
  const state = thunkAPI.getState();
  const type = selectType(state);
  const initialSession = selectInitialSession(state);
  const initialEventTemplate = selectInitialEventTemplate(state);
  const participants = selectParticipants(state);
  const start_date = selectStartDate(state);
  const start_time = selectStartTime(state);
  const end_time = selectEndTime(state);
  const diff = differenceInMinutes(parseISO(end_time), parseISO(start_time));
  const {
    day_of_week = [],
    end_date,
    recurring_type = null,
    recurring_interval = null,
    event_template_id,
  } = selectRecurringSession(state);
  const therapist = selectTherapist(state);
  const service = selectServiceType(state);
  const timezone = selectTimezone(state) === TIMEZONE_OPTIONS.THERAPIST ? therapist.timezone : currentUser(state).timezone;
  const startDateTimeObj = zonedTimeToUtc(parseISO(selectStartDateTime(state)), timezone);
  const endDateTimeObj = addMinutes(startDateTimeObj, diff);
  const h5pPlaylist = gamesToActiviyListGames(selectFormattedGamePlayList(state));
  const dyslexiaPlaylist = selectActivityPlaylist(state);
  const activityStack = service === SERVICES.SPEECH_LANGUAGE.id ? h5pPlaylist : dyslexiaPlaylist;
  const activityPlaylist = type.isDirect ? activityStack : undefined;
  const options = { hour: "numeric", minute: "numeric", second: "numeric", timeZone: "UTC", hourCycle: "h23" };

  const startTime = new Intl.DateTimeFormat("default", options).format(new Date(startDateTimeObj.toISOString()));
  const endTime = new Intl.DateTimeFormat("default", options).format(new Date(endDateTimeObj.toISOString()));

  const treatmentInfo = {
    treatment_id: initialSession.id,
  };
  const data = {
    id: event_template_id,
    type_id: type.id,
    student_groups: participants.filter(item => item.startsWith("g-")).map(item => item.replace("g-", "")),
    students: participants.filter(item => item.startsWith("s-")).map(item => Number(item.replace("s-", ""))),
    start_date: format(parseISO(start_date), "yyyy-MM-dd"),
    end_date: format(parseISO(end_date), "yyyy-MM-dd"),
    start_time: startTime,
    end_time: endTime,
    recurring_pattern: {
      recurring_type,
      recurring_interval,
      day_of_week,
    },
    activity_stack: activityPlaylist,
    therapist: therapist.id,
    service,
    skip_reason: selectSkipReasonText(state),
    skip_reason_id: selectSkipReasonId(state),
  };
  const { recurring_pattern: recuPatternUpd, ...restUpd } = data;
  const { recurring_pattern: recuPatternIn, ...restIn } = initialEventTemplate;

  const recurring_pattern = getDifferences(recuPatternIn, recuPatternUpd);
  const rest = getDifferences(restIn, restUpd);

  let updatedData = { ...rest, ...treatmentInfo };
  if (Object.entries(recurring_pattern).length) {
    updatedData = { ...updatedData, recurring_pattern };
  }
  return updateEventTemplates(event_template_id, updatedData);
};

export const createSessionSlice = createSlice({
  name: SLICE_NAMES.CREATE_SESSION,
  initialState,
  reducers: {
    resetToInitialState: () => ({ ...initialState }),
    setInitialStatus: (state, action) => {
      const {
        mode,
        initialSession: { event_template, ...restInitialSession },
        therapist,
        directTherapist,
        calendarType,
        studentTherapists,
        isRecurrenceEnabled,
        isOpen,
      } = action.payload;
      const { typeId, type_id } = restInitialSession;
      state.therapist = therapist;
      state.timezone = TIMEZONE_OPTIONS.THERAPIST;
      state.initialSession = restInitialSession;
      state.mode = mode;
      state.typeId = typeId || type_id;
      state.calendarType = calendarType;
      state.studentTherapists = studentTherapists;
      state.isRecurring = (mode === CSM.CREATE && isRecurrenceEnabled);
      state.isOpen = isOpen;

      if (_.isEmpty(directTherapist) && therapist?.therapists?.length === 1) {
        state.directTherapist = therapist.therapists[0];
      } else {
        state.directTherapist = directTherapist;
      }
      if (!state.initialSession.startDate && !state.initialSession.start_date) {
        state.initialSession.startDate = convertToNearest15Minutes(moment()?.toDate())?.toISOString();
      }
      if (!_.isEmpty(event_template)) {
        const initialSessionStudentIds = state.initialSession?.students.map(
          student => student.id,
        );
        // We need this data in order to substitute crucial template data by actual treatment data
        const sessionSubstituteData = {
          start_date: state.initialSession.start_date,
          end_date: state.initialSession.end_date,
          students: initialSessionStudentIds,
        };
        const { end_date, recurring_pattern, id: event_template_id } = event_template;
        state.initialEventTemplate = { ...state.initialSession, ...event_template, ...sessionSubstituteData };
        state.recurringSession = { end_date, ...recurring_pattern, event_template_id };
        state.isRecurring = (mode !== CSM.DUPLICATE && isRecurrenceEnabled);
      }
    },
    setServiceType: (state, action) => {
      const startDateTime = parseISO(state.startTime);
      state.endTime = addMinutes(startDateTime, defaultDyslexiaSessionDuration).toISOString();
      state.serviceType = action.payload;
    },
    setH5PActivityList: simpleSetPayload("h5PActivityList"),
    setServiceOptions: simpleSetPayload("serviceOptions"),
    setStartDate: simpleSetPayload("startDate"),
    setStartTime: (state, action) => {
      const startTimeObj = parseISO(state.startTime);
      const endTimeObj = parseISO(state.endTime);
      const diff = differenceInMinutes(endTimeObj, startTimeObj);
      state.startTime = parseISO(action.payload).toISOString();
      state.endTime = addMinutes(parseISO(action.payload), diff).toISOString();
    },
    setEndTime: (state, action) => {
      state.endTime = parseISO(action.payload).toISOString();
    },
    setRecurrenceField: (state, action) => {
      state.recurringSession = { ...state.recurringSession, ...action.payload };
    },
    setParticipants: simpleSetPayload("participants"),
    setActivityPlaylist: simpleSetPayload("activityPlaylist"),
    setMakeUpDate: simpleSetPayload("makeUpDate"),
    setSkipReasonId: simpleSetPayload("skipReasonId"),
    setSkipReasonText: simpleSetPayload("skipReasonText"),
    setTimezone: simpleSetPayload("timezone"),
    setMode: simpleSetPayload("mode"),
    setIsRecurring: simpleSetPayload("isRecurring"),
    setRecurringSession: simpleSetPayload("recurringSession"),
    setPreviewH5PGameExternal: (state, action) => {
      const { h5pGame } = action?.payload;
      state.previewH5PGame = h5pGame;
      state.isPreviewH5PGameDone = false;
    },
    setIsPreviewH5PGameExternalDone: simpleSetPayload("isPreviewH5PGameDone"),
    setRecurringInterval: setRecurrenceInfo("recurring_interval"),
    setRecurringType: setRecurrenceInfo("recurring_type"),
    setRecurringDayOfWeek: setRecurrenceInfo("day_of_week"),
    setRecurringDayOfMonth: setRecurrenceInfo("day_of_month"),
    setRecurringWeekOfMonth: setRecurrenceInfo("week_of_month"),
    setRecurringEndDate: setRecurrenceInfo("end_date"),
    setRecurringEndType: setRecurrenceInfo("end_type"),
    setRecurringOccurrences: setRecurrenceInfo("occurrences"),
    setRecurrenceCompletion: setRecurrenceInfo("completed"),
    setRecurrenceUpdateType: simpleSetPayload("recUpdateType"),
    setStudentTherapists: simpleSetPayload("studentTherapists"),
    setFormattedGamePlayList: simpleSetPayload("formattedGamePlayList"),
    setSelectedFilter: simpleSetPayload("selectedFilter"),
    setSelectedGrades: simpleSetSubField("selectedFilter", "grades"),
    setSelectedThemes: simpleSetSubField("selectedFilter", "themes"),
    setSelectedCCS: simpleSetSubField("selectedFilter", "ccss"),
    setDirectTherapist: (state, action) => {
      const { mode } = state;
      if (mode === CSM.CREATE) {
        state.directTherapist = action.payload;
        if (!_.isEmpty(action.payload)) {
          const therapistServices = action.payload.services;
          if (!_.isEmpty(therapistServices)) {
            const therapistServiceIds = therapistServices.map(service => service.id);
            if (!therapistServiceIds.includes(state.serviceType)) {
              // in case if the new therapist have service of previous one we don't
              // need to change the service
              state.serviceType = action.payload.services[0].id;
            }
          }
          if (action.payload.services) {
            state.serviceOptions = itemArrayToSelectOptions(action.payload.services);
          }
        } else {
          setUniqueServicesFromMultipleTherapists(state);
          state.serviceType = null;
        }
      } else {
        state.therapist = action.payload;
      }
    },
  },
  extraReducers: {
    [fetchCalendarOptions.pending]: (state) => {
      state.isLoadingOptions = true;
      state.isError = false;
    },
    [fetchCalendarOptions.fulfilled]: (state, action) => {
      const {
        students,
        student_groups: studentGroups,
        therapists,
        isLoading,
        therapist,
        activity_stack = [],
      } = action.payload;
      state.isLoadingOptions = isLoading;
      // state.therapist = therapist;
      state.initialActivityPlaylist = activity_stack;
      state.activityPlaylist = activity_stack;
      therapists.forEach(
        slp => {
          const { services } = slp;
          state.studentTherapists.forEach(studentSlp => {
            if (studentSlp.id === slp.id) {
              studentSlp.services = services;
            }
          });
        },
      );
      if (!_.isEmpty(therapist) && therapists?.length === 1) { // in that case we have therapist calendar, need to
        state.serviceOptions = itemArrayToSelectOptions(therapists[0].services); // define the initial services for such case
        state.serviceId = state.serviceOptions[0].id;
      } else if (!_.isEmpty(therapist) && therapists?.length > 1) {
        setUniqueServicesFromMultipleTherapists(state);
      }
      // student options
      const studentsDict = idArrayToDict(students);
      studentGroups.forEach(sg => sg.studentsData = sg.students.map(student => studentsDict[student]));

      state.directStudents = students;
      state.studentGroups = studentGroups;
      state.studentGroupsDict = idArrayToDict(studentGroups);
      const {
        typeId, startDate, type_id, start_date, student_groups: initialStudentGroups = [],
        students: initialStudents = [], service_id, end_date,
      } = state.initialSession;
      const { defaultDuration } = TTI[typeId || type_id] || { defaultDuration: 30 };

      if (service_id) {
        state.serviceType = service_id;
      }
      const startDateObjNonTZ = parseISO(startDate || start_date || new Date().toISOString());
      let startDateObj = utcToZonedTime(startDateObjNonTZ, therapist.timezone);

      let endDateDiff = defaultDuration;
      if (state.mode !== CSM.CREATE && end_date) {
        endDateDiff = differenceInMinutes(parseISO(end_date), startDateObjNonTZ);
      }
      if (state.mode === CSM.DUPLICATE) {
        startDateObj = getDuplicateSessionStartDate(startDateObj);
      }
      state.startDate = startDateObj.toISOString();
      state.startTime = startDateObj.toISOString();
      state.endTime = addMinutes(startDateObj, endDateDiff).toISOString();

      const allStudentsFromGroups = new Set();
      initialStudentGroups?.forEach(sg => state.studentGroupsDict[sg].students.forEach(allStudentsFromGroups.add, allStudentsFromGroups));
      const participants = [
        ...initialStudentGroups.map(sg => `g-${sg}`),
        ...initialStudents.filter(stu => !allStudentsFromGroups.has(stu.id)).map(stu => `s-${stu.id}`),
      ];
      state.participants = participants;
      state.initialParticipants = participants;

      if (state.mode === CSM.CREATE && state.serviceType === SERVICES.DYSLEXIA.id) {
        const endTimeDateObj = parseISO(state.endTime);
        state.endTime = addMinutes(endTimeDateObj, 15).toISOString();
      }
    },
    [fetchCalendarOptions.rejected]: (state, action) => {
      state.isLoadingOptions = false;
      state.isError = true;
      console.error(action.error);
    },
    [fetchAllStudentOptions.pending]: (state) => {
      state.isLoadingAllStudents = true;
      state.isError = false;
    },
    [fetchAllStudentOptions.fulfilled]: (state, action) => {
      state.isLoadingAllStudents = false;
      state.isLoadingOptions = false;
      state.allDistrictStudentsOptions = action.payload;
    },
    [fetchAllStudentOptions.rejected]: (state, action) => {
      state.isLoadingAllStudents = false;
      state.isError = true;
      console.error(action.error);
    },
    [fetchCalendarSessionCreate.pending]: (state) => {
      state.isLoadingCreate = true;
      state.isError = false;
    },
    [fetchCalendarSessionCreate.fulfilled]: (state) => {
      state.isLoadingCreate = false;
    },
    [fetchCalendarSessionCreate.rejected]: (state, action) => {
      state.isLoadingCreate = false;
      state.isError = true;
      console.error(action.error);
    },
    [createGamesActivityList.fulfilled]: (state, action) => {
      state.isGamePlayListReady = true;
      state.formattedGamePlayList = action.payload;
    },
    [createGamesActivityList.rejected]: (state) => {
      state.isGamePlayListReady = true;
      state.formattedGamePlayList = [];
    },
  },
});
export default createSessionSlice.reducer;

// Actions

export const {
  resetToInitialState,
  setInitialStatus,
  setServiceType,
  setServiceOptions,
  setStartDate,
  setStartTime,
  setEndTime,
  setParticipants,
  setActivityPlaylist,
  setMakeUpDate,
  setSkipReasonId,
  setSkipReasonText,
  setTimezone,
  setRecurringSession,
  setMode,
  setIsRecurring,
  setRecurringInterval,
  setRecurringType,
  setRecurringDayOfWeek,
  setRecurringDayOfMonth,
  setRecurringWeekOfMonth,
  setRecurringEndDate,
  setRecurringEndType,
  setRecurringOccurrences,
  setRecurrenceCompletion,
  setRecurrenceUpdateType,
  setRecurrenceField,
  setDirectTherapist,
  setStudentTherapists,
  setH5PActivityList,
  setFormattedGamePlayList,
  setPreviewH5PGameExternal,
  setIsPreviewH5PGameExternalDone,
  setSelectedFilter,
  setSelectedCCS,
  setSelectedGrades,
  setSelectedThemes,
} = createSessionSlice.actions;

// Selectors
export const selectInitialSession = simpleFieldSelector("initialSession");
export const selectTherapist = simpleFieldSelector("therapist");
export const selectIsLoadingCreateSession = simpleFieldSelector("isLoadingCreate");
export const selectIsLoadingOptions = simpleFieldSelector("isLoadingOptions");
export const selectCreateSessionMode = simpleFieldSelector("mode");
export const selectServiceType = simpleFieldSelector("serviceType");
export const selectServiceTypeOptions = simpleFieldSelector("serviceOptions");
export const selectStartDate = simpleFieldSelector("startDate");
export const selectStartTime = simpleFieldSelector("startTime");
export const selectEndTime = simpleFieldSelector("endTime");
export const selectTimezone = simpleFieldSelector("timezone");
export const selectStartDateTime = CREATE_SESSION_SELECTOR(s => {
  const startDateObj = parseISO(s.startDate);
  const startTimeObj = parseISO(s.startTime);
  return setHours(setMinutes(startDateObj, startTimeObj.getMinutes()), startTimeObj.getHours()).toISOString();
});
export const selectRecurringSession = simpleFieldSelector("recurringSession");
export const selectIsRecurring = simpleFieldSelector("isRecurring");
export const selectRecUpdateType = simpleFieldSelector("recUpdateType");
export const selectInitialEventTemplate = simpleFieldSelector("initialEventTemplate");
export const selectDirectTherapist = simpleFieldSelector("directTherapist");
export const selectCalendarType = simpleFieldSelector("calendarType");
export const selectStudentTherapists = simpleFieldSelector("studentTherapists");

export const selectDirectStudents = simpleFieldSelector("directStudents");
export const selectStudentGroups = simpleFieldSelector("studentGroups");
export const selectStudentGroupsDict = simpleFieldSelector("studentGroupsDict");
export const selectParticipants = simpleFieldSelector("participants");
export const selectAllDistrictStudentsOptions = simpleFieldSelector("allDistrictStudentsOptions");
export const selectActivityPlaylist = simpleFieldSelector("activityPlaylist");
export const selectMakeUpDate = simpleFieldSelector("makeUpDate");
export const selectIsError = simpleFieldSelector("isError");
export const selectSkipReasonId = simpleFieldSelector("skipReasonId");
export const selectSkipReasonText = simpleFieldSelector("skipReasonText");
export const selectH5PActivityList = simpleFieldSelector("h5PActivityList");
export const selectFormattedGamePlayList = simpleFieldSelector("formattedGamePlayList");
export const selectIsOpen = simpleFieldSelector("isOpen");
export const selectIsGamePlayListReady = simpleFieldSelector("isGamePlayListReady");
export const selectPreviewH5PGameExternal = simpleFieldSelector("previewH5PGame");
export const selectIsPreviewH5PGameExternalDone = simpleFieldSelector("isPreviewH5PGameDone");
export const selectExternalSelectedFilter = simpleFieldSelector("selectedFilter");
export const selectSelectedGrades = simpleSubFieldSelector("selectedFilter", "grades");
export const selectSelectedThemes = simpleSubFieldSelector("selectedFilter", "themes");
export const selectSelectedCCS = simpleSubFieldSelector("selectedFilter", "ccss");
export const selectIsInfoChanged = CREATE_SESSION_SELECTOR(s => {
  if (s.mode !== CSM.EDIT) {
    return true;
  }
  if (s.participants.length !== s.initialParticipants.length) {
    return true;
  }
  const isRecInfoChanged = isRecurrentSessionInfoChanged(s);
  if (isRecInfoChanged) {
    return true;
  }
  const initialSet = new Set(s.initialParticipants);
  if (s.participants.some(p => !initialSet.has(p))) {
    return true;
  }

  const { start_date, startDate, end_date, endDate } = s.initialSession;
  const initialStartTimeObj = utcToZonedTime(parseISO(start_date || startDate), s.therapist.timezone);
  const initialEndTimeObj = utcToZonedTime(parseISO(end_date || endDate), s.therapist.timezone);
  if (initialStartTimeObj.toISOString() !== s.startDate) {
    return true;
  }

  const nowDate = new Date().getDate();
  if (!isEqual(setDate(initialStartTimeObj, nowDate), setDate(parseISO(s.startTime), nowDate))) {
    return true;
  }
  if (!isEqual(setDate(initialEndTimeObj, nowDate), setDate(parseISO(s.endTime), nowDate))) {
    return true;
  }
  return false;
});

const isRecurrentSessionInfoChanged = (state) => {
  const { initialEventTemplate } = state;
  if (_.isEmpty(initialEventTemplate)) {
    return false;
  }
  const popularizedObject = { ...initialEventTemplate, ...initialEventTemplate.recurring_pattern };
  const { day_of_week: currentDaysOfWeek, event_template_id, completed, ...actualEventTemplate } = state.recurringSession;
  const initialEventCompareObject = {};
  const validKeys = Object.keys(state.recurringSession).filter(value => value !== "day_of_week");
  Object.keys(popularizedObject).forEach((key) => {
    if (validKeys.includes(key)) {
      initialEventCompareObject[key] = popularizedObject[key];
    }
  });
  const initialDaysOfWeek = initialEventTemplate.recurring_pattern.day_of_week;

  const recurringEventChanged = !_.isEqual(actualEventTemplate, initialEventCompareObject);
  const daysOfWeekChanged = !_.isEqual(initialDaysOfWeek, currentDaysOfWeek);
  return recurringEventChanged || daysOfWeekChanged;
};

export const selectTotalNumParticipants = CREATE_SESSION_SELECTOR(s => {
  const groupDict = s.studentGroupsDict;
  const studentSet = new Set();
  let numDirectStudents = 0;

  s.participants?.forEach(item => {
    if (item.startsWith("s-")) {
      numDirectStudents += 1;
      return;
    }
    groupDict[item.replace("g-", "")].students.forEach(studentSet.add, studentSet);
  });
  return numDirectStudents + studentSet.size;
});

export const selectDirectStudentsDict = CREATE_SESSION_SELECTOR(s => idArrayToDict(s.directStudents));
export const selectType = CREATE_SESSION_SELECTOR(t => TTI[t.typeId]);

const getDuplicateSessionStartDate = (startDateObj) => {
  const isPoppins = window?.app?.isPoppins;

  let daysToAdd = 1;
  if (startDateObj.getDay() === 5) {
    daysToAdd = isPoppins ? 2 : 3;
  }

  return addDays(startDateObj, daysToAdd);
};

export const selectParticipantsNames = createSelector(
  [selectParticipants, selectStudentGroupsDict, selectDirectStudents], (participants, studentGroups, students) =>
    participants.reduce((participantsNames, p) => {
      const entityId = parseInt(p.substring(2, p.length)); // e.g. p = "s-1234"
      if (p.startsWith("s")) {
        participantsNames = [...participantsNames, students.find(s => s.id === entityId)?.name];
      } else { // it's a group
        participantsNames = [...participantsNames, studentGroups[entityId]?.studentsData.map(sd => sd?.name).join(", ")];
      }
      return [...participantsNames];
    }, []),
);


export const selectZonedStartDate = createSelector([selectStartDate], startDate => format(utcToZonedTime(startDate), "MM/dd/yyyy"));
export const selectZonedStartTime = createSelector([selectStartTime], startTime => format(utcToZonedTime(startTime), "HH:mm:ss"));
export const selectZonedEndTime = createSelector([selectEndTime], endTime => format(utcToZonedTime(endTime), "HH:mm:ss"));

export const selectIsEditSessionOK = CREATE_SESSION_SELECTOR(s => selectIsInfoChanged(s) && s.skipReasonId && s.skipReasonText);
export const selectIsActivityPlayListChange = CREATE_SESSION_SELECTOR(s => JSON.stringify(s.initialActivityPlaylist) !== JSON.stringify(s.activityPlaylist)); // can do better... there's an "id" param we can ditch (bug if you remove and add same activity)
